import { FormikConfig, FormikProps, FormikValues } from 'formik';
import * as React from 'react';

import useEventListener from '../../../hooks/useEventListener';
import { ButtonSize, ButtonVariant } from '../../Button/Button';
import { ButtonProps } from '../../Button/Button.types';
import DefaultForm from './DefaultForm';
import FullPageForm from './FullPageForm';
import FullScreenForm from './FullScreenForm';

export interface BaseFormProps<T> extends FormikConfig<T> {
  renderForm: (props: FormikProps<T>) => JSX.Element;
  cancelHandler?: () => void;
  closeHandler?: () => void;
  type?: 'default' | 'fullScreen' | 'fullPage';
  header?: JSX.Element | null | ((props: FormikProps<T>) => JSX.Element | null);
  footer?: JSX.Element | null | ((props: FormikProps<T>) => JSX.Element | null);
  submitText?: string | ((props: FormikProps<T>) => string) | JSX.Element;
  cancelText?: string;
  cancelWithEscape?: boolean;
  disabled?: boolean | ((props: FormikProps<T>) => boolean);
  loading?: boolean;
  pt?: number;
  pr?: number;
  pb?: number;
  pl?: number;
  controlsAlignment?: 'left' | 'right';
  controlsWidth?: number;
  submitVariant?: ButtonProps['variant'];
  cancelVariant?: ButtonProps['variant'];
  customControls?: boolean;
  buttonsSize?: ButtonProps['size'];
  cancelTestId?: string;
  submitTestId?: string;
  revertButtonsPosition?: boolean;
  formTestId?: string;
  shouldFixFooter?: boolean;
}

const BaseForm = <T extends FormikValues>({
  type = 'default',
  cancelWithEscape = false,
  disabled = false,
  loading = false,
  pt = 40,
  pr = 40,
  pb = 60,
  pl = 40,
  controlsAlignment = 'right',
  controlsWidth = 120,
  cancelVariant = ButtonVariant.Outline,
  revertButtonsPosition = false,
  buttonsSize = ButtonSize.Large,
  ...rest
}: BaseFormProps<T>): JSX.Element => {
  useEventListener('keydown', event => {
    if (event.key === 'Escape') {
      cancelWithEscape && (rest.closeHandler || rest.cancelHandler)?.();
    }
  });

  const fullScreen = type === 'fullScreen';
  const fullPage = type === 'fullPage';

  const propsWithDefaults = {
    disabled,
    loading,
    pt,
    pr,
    pb,
    pl,
    controlsAlignment,
    controlsWidth,
    cancelVariant,
    revertButtonsPosition,
    cancelWithEscape,
    buttonsSize
  };

  const props = { ...propsWithDefaults, ...rest };

  if (fullScreen) {
    return <FullScreenForm {...props} />;
  }

  if (fullPage) {
    return <FullPageForm {...props} />;
  }

  return <DefaultForm {...props} />;
};

export default BaseForm;
